/**
 * Dynamically sets document title, meta description, Open Graph tags,
 * Twitter Card tags, OG image, and canonical URL. Restores previous
 * values on unmount so route changes don't leave stale metadata.
 */
import { useEffect } from "react";

interface UseDocumentMetaOptions {
  title: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  robots?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  twitterCard?: string;
}

const useDocumentMeta = ({
  title,
  description,
  canonical,
  ogImage,
  robots,
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterCard,
}: UseDocumentMetaOptions) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    // Meta description
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const prevDesc = metaDesc?.content;
    if (description) {
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.name = "description";
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = description;
    }

    // OG title
    let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement | null;
    const prevOgTitle = ogTitle?.content;
    if (ogTitle) ogTitle.content = title;

    // OG description
    let ogDesc = document.querySelector('meta[property="og:description"]') as HTMLMetaElement | null;
    const prevOgDesc = ogDesc?.content;
    if (description && ogDesc) ogDesc.content = description;

    // OG image
    let ogImg = document.querySelector('meta[property="og:image"]') as HTMLMetaElement | null;
    const prevOgImg = ogImg?.content;
    if (ogImage) {
      if (!ogImg) {
        ogImg = document.createElement("meta");
        ogImg.setAttribute("property", "og:image");
        document.head.appendChild(ogImg);
      }
      const absoluteUrl = ogImage.startsWith("http")
        ? ogImage
        : `${window.location.origin}${ogImage}`;
      ogImg.content = absoluteUrl;
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.rel = "canonical";
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = canonical;
    }

    // Robots meta
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const prevRobots = robotsMeta?.content;
    if (robots) {
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.name = "robots";
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = robots;
    }

    // Twitter title
    let twitterTitleEl = document.querySelector('meta[name="twitter:title"]') as HTMLMetaElement | null;
    const prevTwitterTitle = twitterTitleEl?.content;
    const twitterTitleValue = twitterTitle || title;
    if (twitterTitleValue) {
      if (!twitterTitleEl) {
        twitterTitleEl = document.createElement("meta");
        twitterTitleEl.name = "twitter:title";
        document.head.appendChild(twitterTitleEl);
      }
      twitterTitleEl.content = twitterTitleValue;
    }

    // Twitter description
    let twitterDescEl = document.querySelector('meta[name="twitter:description"]') as HTMLMetaElement | null;
    const prevTwitterDesc = twitterDescEl?.content;
    const twitterDescValue = twitterDescription || description;
    if (twitterDescValue) {
      if (!twitterDescEl) {
        twitterDescEl = document.createElement("meta");
        twitterDescEl.name = "twitter:description";
        document.head.appendChild(twitterDescEl);
      }
      twitterDescEl.content = twitterDescValue;
    }

    // Twitter image
    let twitterImgEl = document.querySelector('meta[name="twitter:image"]') as HTMLMetaElement | null;
    const prevTwitterImg = twitterImgEl?.content;
    const twitterImgValue = twitterImage || ogImage;
    if (twitterImgValue) {
      if (!twitterImgEl) {
        twitterImgEl = document.createElement("meta");
        twitterImgEl.name = "twitter:image";
        document.head.appendChild(twitterImgEl);
      }
      const absoluteUrl = twitterImgValue.startsWith("http")
        ? twitterImgValue
        : `${window.location.origin}${twitterImgValue}`;
      twitterImgEl.content = absoluteUrl;
    }

    // Twitter card
    let twitterCardEl = document.querySelector('meta[name="twitter:card"]') as HTMLMetaElement | null;
    const prevTwitterCard = twitterCardEl?.content;
    const twitterCardValue = twitterCard || "summary_large_image";
    if (twitterCardValue) {
      if (!twitterCardEl) {
        twitterCardEl = document.createElement("meta");
        twitterCardEl.name = "twitter:card";
        document.head.appendChild(twitterCardEl);
      }
      twitterCardEl.content = twitterCardValue;
    }

    return () => {
      document.title = previousTitle;
      if (prevDesc && metaDesc) metaDesc.content = prevDesc;
      if (prevOgTitle && ogTitle) ogTitle.content = prevOgTitle;
      if (prevOgDesc && ogDesc) ogDesc.content = prevOgDesc;
      if (prevOgImg && ogImg) ogImg.content = prevOgImg;
      else if (ogImg && ogImage && !prevOgImg) ogImg.remove();
      if (canonicalLink && canonical) canonicalLink.remove();
      if (prevRobots && robotsMeta) robotsMeta.content = prevRobots;
      else if (robotsMeta && robots && !prevRobots) robotsMeta.remove();

      if (prevTwitterTitle && twitterTitleEl) twitterTitleEl.content = prevTwitterTitle;
      else if (twitterTitleEl && twitterTitleValue && !prevTwitterTitle) twitterTitleEl.remove();

      if (prevTwitterDesc && twitterDescEl) twitterDescEl.content = prevTwitterDesc;
      else if (twitterDescEl && twitterDescValue && !prevTwitterDesc) twitterDescEl.remove();

      if (prevTwitterImg && twitterImgEl) twitterImgEl.content = prevTwitterImg;
      else if (twitterImgEl && twitterImgValue && !prevTwitterImg) twitterImgEl.remove();

      if (prevTwitterCard && twitterCardEl) twitterCardEl.content = prevTwitterCard;
      else if (twitterCardEl && twitterCardValue && !prevTwitterCard) twitterCardEl.remove();
    };
  }, [title, description, canonical, ogImage, robots, twitterTitle, twitterDescription, twitterImage, twitterCard]);
};

export default useDocumentMeta;
